import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, Card, Grid, Stack, Container, CardHeader, CardContent, TextField } from '@mui/material';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { Page } from '../../../components';
import { getAuditBranches } from '../../branches/store/branchActions';
import { getAuditCategories } from '../../categories/store/categoryActions';
import { getAuditSuppliers } from '../../suppliers/store/supplierActions';
import { createAsset, getAssetDetail, updateAsset } from '../store/assetActions';
import { getCategorySubCategories } from '../../subcategories/store/subCategoryActions';
import { fDateYMD } from '../../../utils/formatTime';

// The values the data actually uses. Asset.condition defaults to WORKING and
// utils/choices.ASSET_CONDITIONS is BROKEN/GOOD, but this list offered
// EXCELLENT/BROKEN, so editing an existing asset opened with Condition blank —
// and it is required, which blocked the whole form.
const ASSET_CONDITIONS = [
  { id: 1, label: 'Working', value: 'WORKING' },
  { id: 2, label: 'Good', value: 'GOOD' },
  { id: 3, label: 'Broken', value: 'BROKEN' },
];

NewAssetForm.propTypes = {
  isEdit: PropTypes.bool,
  fixedAsset: PropTypes.object,
};

export default function NewAssetForm({ isEdit, fixedAsset }) {
  const dispatch = useDispatch();
  // Rendered both at /assets/new and /assets/:id/edit; the route decides.
  const { id: routeAssetId } = useParams();
  const { asset: loadedAsset, isSaving } = useSelector((state) => state.assetReducer);
  const editing = isEdit ?? Boolean(routeAssetId);
  const asset = fixedAsset ?? (editing ? loadedAsset : null);

  const { auditBranches } = useSelector((state) => state.branchReducer);
  const { auditCategories } = useSelector((state) => state.categoryReducer);
  const { auditSubCategories } = useSelector((state) => state.subCategoryReducer);
  const { auditSuppliers } = useSelector((state) => state.supplierReducer);

  // asset_category / asset_sub_category are null on every asset created before
  // the API started filling them, so fall back to matching the denormalised
  // name against the loaded options. Without this the Category select opened
  // blank on existing assets and, being required, blocked the whole form.
  const byName = (options, name) =>
    (name && options?.find((option) => option?.name?.trim().toLowerCase() === name.trim().toLowerCase())?.id) || '';
  const conditionOptions = ASSET_CONDITIONS.some((c) => c.value === asset?.condition)
    ? ASSET_CONDITIONS
    : [...ASSET_CONDITIONS, ...(asset?.condition ? [{ id: 0, label: asset.condition, value: asset.condition }] : [])];
  const categoryValue = asset?.asset_category || byName(auditCategories, asset?.asset_class);
  const subCategoryValue = asset?.asset_sub_category || byName(auditSubCategories, asset?.sub_class);

  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [categoryId, setCategoryId] = useState(null);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const NewAssetSchema = Yup.object().shape({
    preTag: Yup.string().required('Pretag is required'),
    name: Yup.string().required('Asset Name is required'),
    serialNumber: Yup.string(),
    purchaseCost: Yup.number().required('Asset cost is required'),
    purchaseDate: Yup.string().required('Purchase Date is required'),
    invoiceNumber: Yup.string(),
    grnNumber: Yup.string(),
    watts: Yup.number(),
    supplier: Yup.string().required('Supplie is required'),
    category: Yup.string().required('Category is required'),
    subCategory: Yup.string().required('SubCategory is required'),
    branch: Yup.string().required('Branch is required'),
    condition: Yup.string().required('Condition is required'),
  });

  // Every one of these read `fixedAsset?.first_name` - copy-pasted from a
  // customer form - so edit mode prefilled every box with the same undefined
  // value and a save wiped the record.
  const defaultValues = {
    preTag: asset?.pretag || '',
    name: asset?.name || '',
    serialNumber: asset?.serial_no || '',
    purchaseCost: asset?.cost || '',
    purchaseDate: asset?.purchase_date || purchaseDate || '',
    invoiceNumber: asset?.invoice_number || '',
    grnNumber: asset?.grn_number || '',
    watts: asset?.watts || 0,
    supplier: asset?.supplier?.id || '',
    category: categoryValue,
    subCategory: subCategoryValue,
    branch: asset?.branch?.id || '',
    condition: asset?.condition || '',
    assetPhoto: '',
  };

  const methods = useForm({
    resolver: yupResolver(NewAssetSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    // control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  console.log('VALUES:', values);

  useEffect(() => {
    dispatch(getAuditSuppliers({}));
    dispatch(getAuditBranches({}));
    dispatch(getAuditCategories({}));
    if (editing && routeAssetId && !fixedAsset) {
      dispatch(getAssetDetail(routeAssetId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, routeAssetId]);

  useEffect(() => {
    // Re-seed once the asset lands, and again once the category options arrive,
    // since the category id may have to be resolved from them.
    reset(defaultValues);
    if (asset?.purchase_date) setPurchaseDate(new Date(asset.purchase_date));
    // Load this asset's sub-categories so its current one can be shown.
    if (categoryValue) setCategoryId(categoryValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, editing, categoryValue]);

  useEffect(() => {
    // The sub-category list arrives after its category, so re-apply the value.
    if (editing && subCategoryValue) setValue('subCategory', subCategoryValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryValue, editing]);

  useEffect(() => {
    if (categoryId) {
      dispatch(getCategorySubCategories(categoryId));
    }
  }, [categoryId, dispatch]);

  const handleSelectedCategory = (category) => {
    console.log('CAT:', category);
    setCategoryId(category);
    setValue('category', category);
  };
  const onSubmit = async (data) => {
    const payload = {
      pretag: data.preTag,
      name: data.name,
      serial_no: data.serialNumber,
      cost: data.purchaseCost,
      purchase_date: fDateYMD(purchaseDate),
      invoice_number: data.invoiceNumber,
      grn_number: data.grnNumber,
      watts: data.watts,
      supplier_id: data.supplier,
      asset_class_id: data.category,
      sub_class_id: data.subCategory,
      location_id: data.branch,
      condition: data.condition,
      asset_photo: data.assetPhoto,
    };
    const result = editing
      ? await dispatch(updateAsset({ id: asset?.id ?? routeAssetId, payload }))
      : await dispatch(createAsset(payload));

    // Navigate once the write has landed rather than on a 2s timer, and stay
    // put on failure so the entered values are not lost.
    if (result?.ok !== false) {
      enqueueSnackbar(editing ? 'Update success!' : 'Create success!');
      navigate(editing ? `/dashboard/assets/${asset?.id ?? routeAssetId}` : '/dashboard/assets');
    }
  };
  return (
    <Page title={editing ? 'Edit Asset' : 'New Asset'}>
      <Container maxWidth="xl">
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Card sx={{ p: 3 }}>
                <CardHeader title={'Create asset'} subheader={''} />
                <CardContent>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField type='number' name="preTag" label={<span>PreTag<span style={{ color: 'red' }}> *</span></span>} />
                    <RHFTextField name="name" label={<span>Name<span style={{ color: 'red' }}> *</span></span>} />
                    <RHFTextField name="serialNumber" label="Serial Number" />
                    <RHFTextField type="number" name="purchaseCost"  label={<span>Purchase Cost<span style={{ color: 'red' }}> *</span></span>} />
                    <DatePicker
                      label={<span>Purchase Date<span style={{ color: 'red' }}> *</span></span>}
                      value={purchaseDate}
                      onChange={(newValue) => setPurchaseDate(newValue)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <RHFTextField name="invoiceNumber" label="Invoice Number" />
                    <RHFTextField name="grnNumber" label="GRN Number" />
                    <RHFTextField type="number" name="watts" label="Watts" />
                    <RHFSelect name="supplier" label={<span>Supplier<span style={{ color: 'red' }}> *</span></span>} placeholder="Supplier">
                      <option value="" />
                      {auditSuppliers?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.business_name}
                        </option>
                      ))}
                    </RHFSelect>
                    <RHFSelect
                      name="category"
                      label={<span>Category<span style={{ color: 'red' }}> *</span></span>}
                      placeholder="Category"
                      onChange={(event) => handleSelectedCategory(event.target.value)}
                    >
                      <option value="" />
                      {auditCategories?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </RHFSelect>
                    <RHFSelect name="subCategory" label={<span>SubCategory<span style={{ color: 'red' }}> *</span></span>} placeholder="SubCategory">
                      <option value="" />
                      {auditSubCategories?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </RHFSelect>
                    <RHFSelect name="branch" label={<span>Branch<span style={{ color: 'red' }}> *</span></span>} placeholder="Branch">
                      <option value="" />
                      {auditBranches?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </RHFSelect>
                    <RHFSelect name="condition" label={<span>Condition<span style={{ color: 'red' }}> *</span></span>} placeholder="Condition">
                      <option value="" />
                      {conditionOptions?.map((option) => (
                        <option key={option.id} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </RHFSelect>
                    {/* <TextField name="asset_photo" type='file'/> */}
                  </Box>

                  <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                    <LoadingButton type="submit" variant="contained" loading={isSubmitting || isSaving}>
                      {!isEdit ? 'Create Asset' : 'Save Changes'}
                    </LoadingButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </FormProvider>
      </Container>
    </Page>
  );
}
